import { useForm } from 'react-hook-form@latest';
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import type { FC } from 'react';
import { _notNil } from '@/littledash';
import type { DataTableApiId } from '@/model/DataTable.model';
import type { StudyApiId } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { errorToast, successToast } from '@/helpers';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import Loading from '@/components/Loading';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

interface EditDatatableProps {
  studyId: StudyApiId;
  dataTableId: DataTableApiId;
  closeModal: () => void;
  fetchTableData: () => Promise<void>;
  mode?: 'datatable' | 'sample_sheet';
}

interface EditDatatableFormProps {
  studyId: StudyApiId;
  datatable: { id: DataTableApiId; name: string; unit?: string | null; type: string };
  closeModal: () => void;
  fetchTableData: () => Promise<void>;
  mode: 'datatable' | 'sample_sheet';
}

interface EditDatatableFormData {
  name: string;
  unit?: string;
}

const EditDatatable: FC<EditDatatableProps> = ({
  studyId,
  dataTableId,
  closeModal,
  fetchTableData,
  mode = 'datatable',
}) => {
  const { response: datatableGETResponse, loading: fetchingDatatable } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/datatables/{dataTableApiId}',
    path: {
      studyApiId: studyId,
      dataTableApiId: dataTableId,
    },
    options: { onError: { throw: false, toast: true } },
  });

  if (datatableGETResponse?.type === 'error') {
    closeModal();
    return null;
  }

  const datatable = datatableGETResponse?.body;
  const modalTitle = mode === 'sample_sheet' ? 'Edit Sampling Sheet' : 'Edit Data Table';

  return (
    <ModalContainer size="narrow">
      <ModalHeader title={modalTitle} closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      {fetchingDatatable || !_notNil(datatable) ? (
        <Loading />
      ) : (
        <EditDatatableForm
          closeModal={closeModal}
          studyId={studyId}
          datatable={datatable}
          fetchTableData={fetchTableData}
          mode={mode}
        />
      )}
    </ModalContainer>
  );
};

const EditDatatableForm: FC<EditDatatableFormProps> = ({
  studyId,
  datatable,
  closeModal,
  fetchTableData,
  mode = 'datatable',
}) => {
  const {
    invoke: patchDatatable,
    response: datatablePATCHResponse,
    loading: patchingDatatable,
  } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/datatables/{dataTableApiId}',
    path: { studyApiId: studyId, dataTableApiId: datatable.id },
    invokeOnInit: false,
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditDatatableFormData>({
    defaultValues: { name: datatable.name ?? '', unit: datatable.unit ?? undefined },
  });

  const onSubmit = async (formValues: EditDatatableFormData): Promise<void> => {
    try {
      await patchDatatable({
        path: { studyApiId: studyId, dataTableApiId: datatable.id },
        body: formValues,
      });
      await fetchTableData();
      closeModal();
      successToast(mode === 'sample_sheet' ? 'Updated sampling sheet' : 'Updated data table');
    } catch (err) {
      const ivError = new InVivoError(
        mode === 'sample_sheet' ? 'Could not edit sampling sheet' : 'Could not edit data table',
        {
          slug: 'edit-data-table',
          cause: err,
        }
      );
      ExceptionHandler.captureException(ivError);
      errorToast(mode === 'sample_sheet' ? 'Could not edit sampling sheet' : 'Could not edit data table');
    }
  };

  return (
    <>
      {datatablePATCHResponse?.type === 'error' && <ApiErrorBanner />}
      <div className="pa3">
        <label htmlFor="datatable-name">Name</label>
        <input
          type="text"
          id="datatable-name"
          style={{ marginBottom: 0 }}
          className={cn({ input__error: _notNil(errors?.name) })}
          autoFocus
          data-testid="edit-datatable__name-input"
          {...register('name', {
            required: 'This field is required',
            maxLength: { value: 255, message: 'Maximum of 255 characters' },
          })}
        />
        <ErrorMessage
          errors={errors}
          name="name"
          render={({ message }) => (
            <small className="red db pt2" data-testid="edit-datatable__name-error">
              {message}
            </small>
          )}
        />
      </div>
      {datatable.type === 'numeric' && (
        <div className="ph3 pb3">
          <label htmlFor="datatable-unit">Unit</label>
          <input
            type="text"
            id="datatable-unit"
            style={{ marginBottom: 0 }}
            className={cn({ input__error: _notNil(errors?.unit) })}
            data-testid="edit-datatable__unit-input"
            {...register('unit', {
              required: 'This field is required',
              maxLength: { value: 255, message: 'Maximum of 255 characters' },
            })}
          />
          <ErrorMessage
            errors={errors}
            name="unit"
            render={({ message }) => (
              <small className="red db pt2" data-testid="edit-datatable__unit-error">
                {message}
              </small>
            )}
          />
        </div>
      )}
      <ModalActions
        submitButtonProps={{ disabled: patchingDatatable || datatablePATCHResponse?.type === 'error' }}
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        className="pa3 bt b--moon-gray"
      />
    </>
  );
};

export default EditDatatable;
